<template>
<div class="st-page">
  <Navigation />
  <div class="container" style="padding-top:20px;max-width:1100px;">
    <div class="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
      <div class="d-flex align-items-center gap-3">
        <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="st-back"><i class="mdi mdi-arrow-left"></i></a>
        <div>
          <h4 class="st-page-title"><i class="mdi mdi-arrow-up-bold me-2"></i>Promotions</h4>
          <p style="color:#8BA4C4;font-size:12px;margin:0;">Promote students to the next class. Mark repeats and graduates per student.</p>
        </div>
      </div>
    </div>

    <div class="st-card mb-3">
      <div class="row g-3 align-items-end">
        <div class="col-md-4">
          <label class="st-label">From class</label>
          <select class="st-select" v-model="fromClassId" @change="loadStudents">
            <option value="">Select source class</option>
            <option v-for="c in classes" :key="c.id" :value="c.id">{{ c.name }}</option>
          </select>
        </div>
        <div class="col-md-4">
          <label class="st-label">Promote to</label>
          <select class="st-select" v-model="toClassId">
            <option value="">Select target class</option>
            <option v-for="c in classes" :key="c.id" :value="c.id" :disabled="c.id == fromClassId">{{ c.name }}</option>
          </select>
        </div>
        <div class="col-md-3">
          <label class="st-label">Term <small style="color:#8BA4C4;font-weight:400;">(optional)</small></label>
          <select class="st-select" v-model="termId">
            <option value="">—</option>
            <option v-for="t in terms" :key="t.id" :value="t.id">{{ t.name }}{{ t.is_current == 1 ? ' (current)' : '' }}</option>
          </select>
        </div>
        <div class="col-md-1 text-end">
          <button class="st-btn st-btn-blue" @click="loadStudents" :disabled="!fromClassId || loading"><i class="mdi mdi-refresh"></i></button>
        </div>
      </div>
    </div>

    <div v-if="!fromClassId" class="st-empty"><i class="mdi mdi-arrow-up-bold"></i>Pick a source class to load its students</div>

    <div v-else>
      <div v-if="loading" class="st-spinner"><div class="spinner-border"></div></div>
      <div v-else-if="!students.length" class="st-empty"><i class="mdi mdi-account-school"></i>No active students in this class</div>
      <div v-else class="st-card">
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-3 gap-2">
          <div>
            <div style="color:#E8EDF4;font-weight:700;font-size:15px;">{{ students.length }} students in {{ fromClassName }}</div>
            <div style="color:#8BA4C4;font-size:12px;">Default action is <strong style="color:#10B981;">PROMOTE</strong>. Toggle per student to repeat or graduate.</div>
          </div>
          <div class="d-flex gap-2 flex-wrap">
            <button class="st-btn st-btn-sm" :class="bulk === 'promote' ? 'st-btn-active-green' : 'st-btn-blue'" @click="setAll('promote')"><i class="mdi mdi-arrow-up me-1"></i>All Promote</button>
            <button class="st-btn st-btn-sm" :class="bulk === 'repeat' ? 'st-btn-active-amber' : 'st-btn-blue'" @click="setAll('repeat')"><i class="mdi mdi-replay me-1"></i>All Repeat</button>
            <button class="st-btn st-btn-sm" :class="bulk === 'graduate' ? 'st-btn-active-purple' : 'st-btn-blue'" @click="setAll('graduate')"><i class="mdi mdi-school me-1"></i>All Graduate</button>
          </div>
        </div>

        <div class="st-stat-row" style="margin-bottom:14px;">
          <div class="st-stat" style="border-color:rgba(16,185,129,.4);"><div class="st-stat-num" style="color:#10B981;">{{ count('promote') }}</div><div class="st-stat-lbl">Promoting</div></div>
          <div class="st-stat" style="border-color:rgba(245,158,11,.4);"><div class="st-stat-num" style="color:#F59E0B;">{{ count('repeat') }}</div><div class="st-stat-lbl">Repeating</div></div>
          <div class="st-stat" style="border-color:rgba(168,85,247,.4);"><div class="st-stat-num" style="color:#A855F7;">{{ count('graduate') }}</div><div class="st-stat-lbl">Graduating</div></div>
        </div>

        <div style="overflow-x:auto;">
          <table class="st-table">
            <thead><tr><th>Name</th><th>Adm No</th><th>Gender</th><th>Wallet</th><th style="text-align:center;width:340px;">Action</th></tr></thead>
            <tbody>
              <tr v-for="s in students" :key="s.id">
                <td style="font-weight:600;">{{ s.name }}</td>
                <td>{{ s.admission_no }}</td>
                <td style="text-transform:capitalize;">{{ s.gender || '-' }}</td>
                <td style="color:#4FC3F7;">KSh {{ fNum(s.wallet_balance) }}</td>
                <td>
                  <div class="action-toggle">
                    <button class="action-btn" :class="{'action-promote': s._action === 'promote'}" @click="s._action = 'promote'">↑ Promote</button>
                    <button class="action-btn" :class="{'action-repeat': s._action === 'repeat'}" @click="s._action = 'repeat'">↻ Repeat</button>
                    <button class="action-btn" :class="{'action-graduate': s._action === 'graduate'}" @click="s._action = 'graduate'">🎓 Graduate</button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="d-flex flex-wrap align-items-center justify-content-between mt-3 gap-2">
          <div v-if="resultMsg" class="st-alert" :class="resultOk ? 'st-alert-success' : 'st-alert-danger'" style="margin:0;flex:1;">{{ resultMsg }}</div>
          <button class="btn st-btn-primary" @click="runPromotion" :disabled="processing || count('promote') > 0 && !toClassId">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Processing...' : 'Run Promotion' }}
          </button>
        </div>
        <small v-if="count('promote') > 0 && !toClassId" style="color:#F59E0B;font-size:11px;margin-top:6px;display:block;">Pick a target class — {{ count('promote') }} students set to promote.</small>
      </div>
    </div>
  </div>
</div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/simi-auth/Navigation.vue";
import { executeJson, simitrack } from "@/api";
export default {
  name: "Promotions",
  components: { Navigation },
  data() {
    return {
      classes: [], terms: [],
      fromClassId: '', toClassId: '', termId: '',
      students: [], loading: false,
      processing: false, resultMsg: '', resultOk: false,
      bulk: 'promote',
    };
  },
  computed: {
    fromClassName() {
      const c = this.classes.find(x => String(x.id) === String(this.fromClassId));
      return c ? c.name : '';
    },
  },
  mounted() { this.loadClasses(); this.loadTerms(); },
  methods: {
    fNum(v) { if (!v) return '0'; return Number(v).toLocaleString('en-KE'); },
    async loadClasses() {
      try {
        const r = await executeJson({ function: 'getClasses' }, simitrack);
        if (r.data.success) this.classes = Array.isArray(r.data.data) ? r.data.data : (r.data.data.classes || []);
      } catch (e) { console.error(e); }
    },
    async loadTerms() {
      try {
        const r = await executeJson({ function: 'getTerms' }, simitrack);
        if (r.data.success) {
          const list = Array.isArray(r.data.data) ? r.data.data : (r.data.data.terms || []);
          this.terms = list;
          const cur = list.find(t => t.is_current == 1);
          if (cur) this.termId = cur.id;
        }
      } catch (e) { /* silent */ }
    },
    async loadStudents() {
      if (!this.fromClassId) { this.students = []; return; }
      this.loading = true;
      this.resultMsg = '';
      try {
        const r = await executeJson({ function: 'getStudents', classId: this.fromClassId, status: 'active', limit: 500 }, simitrack);
        if (r.data.success) {
          const list = (r.data.data && (r.data.data.students || r.data.data)) || [];
          this.students = list.map(s => ({ ...s, _action: 'promote' }));
          this.bulk = 'promote';
        }
      } catch (e) { console.error(e); }
      this.loading = false;
    },
    setAll(action) {
      this.bulk = action;
      this.students = this.students.map(s => ({ ...s, _action: action }));
    },
    count(action) {
      return this.students.filter(s => s._action === action).length;
    },
    async runPromotion() {
      if (!this.fromClassId) { this.resultMsg = 'Pick the source class.'; this.resultOk = false; return; }
      const promoteCount = this.count('promote');
      if (promoteCount > 0 && !this.toClassId) { this.resultMsg = 'Pick the target class for the students being promoted.'; this.resultOk = false; return; }
      const summary = `${promoteCount} promote · ${this.count('repeat')} repeat · ${this.count('graduate')} graduate`;
      if (!confirm(`Confirm: ${summary}. Proceed?`)) return;

      const retainIds = this.students.filter(s => s._action === 'repeat').map(s => s.id);
      const graduateIds = this.students.filter(s => s._action === 'graduate').map(s => s.id);

      this.processing = true;
      this.resultMsg = '';
      try {
        const params = {
          function: 'promoteStudents',
          fromGradeId: this.fromClassId,
          toGradeId: this.toClassId || this.fromClassId, // server requires it; harmless if everyone retains/graduates
          termId: this.termId || '',
          retainStudentIds: JSON.stringify(retainIds),
          graduateStudentIds: JSON.stringify(graduateIds),
        };
        const r = await executeJson(params, simitrack);
        if (r.data.success) {
          const d = r.data.data || {};
          this.resultMsg = `Done — ${d.promoted || 0} promoted, ${d.retained || 0} repeated, ${d.graduated || 0} graduated.`;
          this.resultOk = true;
          await this.loadStudents();
        } else {
          this.resultMsg = r.data.message || 'Promotion failed';
          this.resultOk = false;
        }
      } catch (e) { this.resultMsg = 'Connection error'; this.resultOk = false; }
      this.processing = false;
    },
  }
};
</script>

<style scoped>
.st-page{min-height:100vh;background:#0A1628;padding-top:70px;}
.st-back{color:#8BA4C4;font-size:22px;text-decoration:none;padding:4px;}.st-back:hover{color:#4FC3F7;}
.st-page-title{color:#E8EDF4;font-size:18px;font-weight:700;margin:0;}
.st-card{background:#0F2347;border:1px solid #1A3A6A;border-radius:14px;padding:24px;margin-bottom:16px;}
.st-stat-row{display:flex;flex-wrap:wrap;gap:12px;}
.st-stat{background:#0A1628;border:1px solid #1A3A6A;border-radius:12px;padding:14px 18px;flex:1;min-width:140px;}
.st-stat-num{font-size:24px;font-weight:800;color:#4FC3F7;}
.st-stat-lbl{font-size:11px;color:#8BA4C4;margin-top:2px;}
.st-table{width:100%;border-collapse:collapse;}
.st-table th{color:#8BA4C4;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:1px;padding:10px 12px;border-bottom:1px solid #1A3A6A;text-align:left;white-space:nowrap;}
.st-table td{color:#E8EDF4;font-size:13px;padding:10px 12px;border-bottom:1px solid #1A3A6A;}
.st-btn{border:none;border-radius:8px;padding:8px 14px;font-size:13px;font-weight:600;cursor:pointer;transition:all .15s;}
.st-btn-sm{padding:5px 12px;font-size:12px;}
.st-btn-blue{background:rgba(46,134,222,.15);color:#4FC3F7;border:1px solid rgba(46,134,222,.3);}
.st-btn-blue:hover{background:rgba(46,134,222,.25);}
.st-btn-active-green{background:rgba(16,185,129,.2);color:#10B981;border:1px solid rgba(16,185,129,.4);}
.st-btn-active-amber{background:rgba(245,158,11,.2);color:#F59E0B;border:1px solid rgba(245,158,11,.4);}
.st-btn-active-purple{background:rgba(168,85,247,.2);color:#A855F7;border:1px solid rgba(168,85,247,.4);}
.st-btn-primary{background:linear-gradient(135deg,#2E86DE 0%,#4FC3F7 100%);color:#fff;border:none;border-radius:10px;padding:10px 22px;font-weight:700;}
.st-empty{text-align:center;padding:40px 20px;color:#8BA4C4;}
.st-empty i{font-size:48px;color:#1A3A6A;display:block;margin-bottom:12px;}
.st-label{color:#8BA4C4;font-size:13px;font-weight:600;margin-bottom:6px;display:block;}
.st-input,.st-select{width:100%;background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:10px 14px;color:#E8EDF4;font-size:14px;outline:none;}
.st-input:focus,.st-select:focus{border-color:#2E86DE;}
.st-search{background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:9px 14px 9px 36px;color:#E8EDF4;font-size:14px;outline:none;width:100%;max-width:300px;}
.st-search-wrap{position:relative;}.st-search-wrap i{position:absolute;left:12px;top:50%;transform:translateY(-50%);color:#8BA4C4;}
.st-spinner{display:flex;justify-content:center;padding:40px;}.st-spinner .spinner-border{color:#2E86DE;}
.st-alert{padding:10px 14px;border-radius:10px;font-size:13px;}
.st-alert-danger{background:rgba(255,107,107,.1);border:1px solid rgba(255,107,107,.2);color:#FF9999;}
.st-alert-success{background:rgba(16,185,129,.1);border:1px solid rgba(16,185,129,.2);color:#10B981;}

.action-toggle{display:flex;gap:4px;justify-content:center;}
.action-btn{flex:1;background:#0A1628;border:1px solid #1A3A6A;color:#8BA4C4;border-radius:6px;padding:6px 8px;font-size:11px;font-weight:600;cursor:pointer;transition:all .15s;white-space:nowrap;}
.action-btn:hover{border-color:#4FC3F7;color:#E8EDF4;}
.action-btn.action-promote{background:rgba(16,185,129,.2);color:#10B981;border-color:rgba(16,185,129,.5);}
.action-btn.action-repeat{background:rgba(245,158,11,.2);color:#F59E0B;border-color:rgba(245,158,11,.5);}
.action-btn.action-graduate{background:rgba(168,85,247,.2);color:#A855F7;border-color:rgba(168,85,247,.5);}
</style>
